import {
  Area,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  LayoutCard,
  TimeXAxis,
  YAxis,
  Stack,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';
import type { SpendBreakdownChartPoint } from '@cast/features-cluster-shared-utils';

import {
  DashboardChartSummaryMetric,
  DashboardChartSummaryMetricsRow,
} from './DashboardChartCard';
import { DashboardChartFrame } from './DashboardChartFrame';
import { DashboardChartRangePicker } from './DashboardChartRangePicker';
import { DashboardDateChartTooltipHeader } from './DashboardDateChartTooltipHeader';
import { DashboardSectionError } from './DashboardSectionError';
import { DASHBOARD_CHART_TIMEZONE } from '../constants/dashboardCharts';
import { dashboardChartZoomProps } from '../hooks/useDashboardChartZoomState';
import { useDashboardCostChartData } from '../hooks/useDashboardCostChartData';
import { useDashboardFleetChartColors } from '../hooks/useDashboardFleetChartColors';
import { formatMonthlyCost } from '../utils/formatMonthlyCost';

const FALLBACK_TOOLTIP = 'Temporary on-demand node while spot is not available';

export const MonthlyClusterCostChartSection = () => {
  const fleetChartColors = useDashboardFleetChartColors();
  const {
    chartData,
    currentSpendMonthly,
    onDemandSpendMonthly,
    spotSpendMonthly,
    fallbackSpendMonthly,
    isLoading,
    error,
    refetch,
  } = useDashboardCostChartData();

  return (
    <LayoutCard
      title="Monthly cluster cost"
      headerEndContent={<DashboardChartRangePicker />}
    >
      <DashboardSectionError hasError={!!error} onRetry={refetch}>
        <Stack gap={20}>
          <DashboardChartSummaryMetricsRow>
            <DashboardChartSummaryMetric
              label="Current spend"
              unit="/mo"
              value={formatMonthlyCost(currentSpendMonthly)}
            />
            <DashboardChartSummaryMetric
              color={fleetChartColors.onDemand}
              label="On-demand"
              unit="/mo"
              value={formatMonthlyCost(onDemandSpendMonthly)}
            />
            <DashboardChartSummaryMetric
              color={fleetChartColors.spot}
              label="Spot"
              unit="/mo"
              value={formatMonthlyCost(spotSpendMonthly)}
            />
            <DashboardChartSummaryMetric
              color={fleetChartColors.fallback}
              label="Fallback"
              unit="/mo"
              value={formatMonthlyCost(fallbackSpendMonthly)}
              infoTooltip={FALLBACK_TOOLTIP}
            />
          </DashboardChartSummaryMetricsRow>

          <DashboardChartFrame>
            <ChartContainer
              data={chartData ?? []}
              isLoading={isLoading}
              hasNoData={!isLoading && !chartData?.length}
              noDataOverlayProps={{ title: 'No cost data available' }}
              margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
              fallbackChartType="area"
              testId="monthly-cluster-cost-chart"
              zoomProps={dashboardChartZoomProps}
              chartActionsSx={{ top: 0, right: 0 }}
            >
              <Area
                dataKey="spot"
                fill={fleetChartColors.spot}
                fillOpacity={0.95}
                stackId="spend"
                stroke={fleetChartColors.spot}
                strokeWidth={1.5}
                tooltipConfig={{ displayName: 'Spot' }}
              />
              <ChartGrid hideVertical />
              <Area
                dataKey="fallback"
                fill={fleetChartColors.fallback}
                fillOpacity={0.95}
                stackId="spend"
                stroke={fleetChartColors.fallback}
                strokeWidth={1.5}
                tooltipConfig={{ displayName: 'Fallback' }}
              />
              <Area
                dataKey="onDemand"
                fill={fleetChartColors.onDemand}
                fillOpacity={0.95}
                stackId="spend"
                stroke={fleetChartColors.onDemand}
                strokeWidth={1.5}
                tooltipConfig={{ displayName: 'On-demand' }}
              />
              <YAxis
                type="number"
                padding={{ top: 16 }}
                unitFormatter={(value) => formatMonthlyCost(Number(value))}
              />
              <TimeXAxis
                unixDataKey="chartTimestampUnix"
                timezone={DASHBOARD_CHART_TIMEZONE}
              />
              <ChartTooltip<SpendBreakdownChartPoint>
                header={DashboardDateChartTooltipHeader}
                formatValue={(value) =>
                  dashesOrNum({
                    value,
                    mapNumber: formatMonthlyCost,
                  })
                }
              />
            </ChartContainer>
          </DashboardChartFrame>
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
